import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Background,
  type Node,
  type NodeChange,
  type Edge,
  ReactFlow,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
  Controls,
  MiniMap,
  BackgroundVariant,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { nodeTypes } from "./nodes";
import { LeftSidebar } from "./common/panels/leftSidebar";
import { createNodeByType } from "./utils/nodeFactory";
import { PENDING_TEMPLATE_KEY } from "./templates/workflowTemplates";
import {
  ArrowLeft,
  Plus,
  RocketIcon,
  Workflow,
  Upload,
  Download,
  PlayCircle,
  Save,
  Cpu,
} from "lucide-react";
import { DeleteButtonEdge } from "./edge/deleteEdge";
import { useNavigate, useParams } from "react-router-dom";
import { getNodeTypeEnum } from "./enums/WorkflowNodeTypeEnums";
import {
  validateWorkflow,
  formatValidationErrors,
} from "./utils/nodeValidation";
import apiClient from "../api/api";
import { API_ENDPOINTS } from "../api/api.config";
import { ExitConfirmationModal } from "./components/exitConfirmationModal";
import ExecutionsPanel from "./common/panels/ExecutionsPanel";
import WorkflowTokenAnalytics from "./common/panels/WorkflowTokenAnalytics";
import { WorkflowTitle } from "./common/ui/WorkflowTitle";
import { ToastNotification, ToastType } from "./common/ui/ToastNotification";
import { Button } from "./common/ui/Button";
import Can from "../components/auth/Can";
import {
  getUINodeType,
  injectTemplateIdToHttpNodes,
  processInitialEdges,
} from "./utils/workflowHelpers";

const proOptions = { hideAttribution: true };

const edgeTypes = {
  deleteButtonEdge: DeleteButtonEdge,
};

const POLLING_INTERVAL = 2000;
const MAX_POLLING_ATTEMPTS = 150;

const deepClone = <T,>(value: T): T => {
  if (value === undefined || value === null) {
    return value;
  }
  return JSON.parse(JSON.stringify(value));
};

export function DocumentParsingWorkflow() {
  const { id: workflowId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [nodes, setNodes] = useState<Node<any>[]>([]);
  const [workflowDbId, setWorkflowDbId] = useState<string | null>(null);
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(false);
  const [executionResults, setExecutionResults] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<"nodes" | "executions">("nodes");
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<any | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Refs to hold handlers to avoid declaration order issues
  const handleExecuteNodeRef = useRef<((id: string) => Promise<void>) | null>(
    null,
  );
  // Process nodes to unwrap data.input structure from API
  const processImportedNodes = (rawNodes: any[]): Node<any>[] => {
    // Deduplicate nodes by ID - keep the first occurrence
    const seen = new Set<string>();
    const uniqueNodes = rawNodes.filter((node) => {
      if (seen.has(node.id)) return false;
      seen.add(node.id);
      return true;
    });

    return uniqueNodes.map((node) => {
      const baseData = node.data?.inputs
        ? { ...node.data.inputs }
        : { ...node.data };
      delete baseData.inputs;
      delete baseData.outputs;

      const nodeName =
        node.type === "HTTP"
          ? "HttpNode"
          : node.name || baseData.name || node.id;

      return {
        id: node.id,
        type: getUINodeType(node.type),
        position: node.position,
        data: {
          ...baseData,
          name: nodeName,
          outputs: node.data?.outputs,
          onExecuteNode: (id: string) => {
            console.log(`⚡ Executing node ${id} via callback`);
            handleExecuteNodeRef.current?.(id);
          },
        },
      };
    });
  };

  const [edges, setEdges] = useState<Edge[]>(processInitialEdges([]));
  const [, setSelectedNode] = useState<Node<any> | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [title, setTitle] = useState("New Workflow");
  const [version, setVersion] = useState("Version 1.0");
  const [, setValidationErrors] = useState<string>("");
  const [showExitConfirmModal, setShowExitConfirmModal] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showTokenAnalytics, setShowTokenAnalytics] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<ToastType>("info");

  const showToast = (
    message: string,
    type: ToastType = "info",
    duration = 5000,
  ) => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(null), duration);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load workflow if ID is present in URL
  useEffect(() => {
    if (workflowId) {
      loadWorkflowById(workflowId);
    }
  }, [workflowId]);

  // Hydrate from a gallery template (a fresh, unsaved workflow). Builds nodes via
  // the same factory the "add node" button uses so default config is valid, then
  // the user reviews and saves (which creates the workflow). Skip when editing.
  useEffect(() => {
    if (workflowId) return;
    const raw = localStorage.getItem(PENDING_TEMPLATE_KEY);
    if (!raw) return;
    try {
      const tmpl = JSON.parse(raw);
      const builtNodes: Node<any>[] = (tmpl.nodes || []).map((n: any) => {
        const node = createNodeByType(n.factoryKey, n.position || { x: 0, y: 0 }, []);
        node.id = n.id;
        node.data = {
          ...node.data,
          ...(n.data || {}),
          onExecuteNode: (id: string) => handleExecuteNodeRef.current?.(id),
        };
        return node;
      });
      const builtEdges: Edge[] = (tmpl.edges || []).map((e: any, i: number) => ({
        id: `edge-${e.source}-${e.target}-${i}`,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        animated: false,
        type: "deleteButtonEdge",
        data: { onEdgeDelete: onEdgesDelete },
      }));
      if (tmpl.title) setTitle(tmpl.title);
      if (tmpl.description) setVersion(tmpl.description);
      setNodes(builtNodes);
      setEdges(builtEdges);
      showToast(`Loaded template: ${tmpl.title || "workflow"} — configure and save`, "success");
    } catch (err) {
      console.error("Template hydration failed", err);
    } finally {
      localStorage.removeItem(PENDING_TEMPLATE_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load workflow from API
  const loadWorkflowById = async (id: string) => {
    setIsLoadingWorkflow(true);
    try {
      const response = await apiClient.get(
        `${API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD}/${id}`,
      );

      if (response.data.success && response.data.data) {
        const workflowData = response.data.data;

        setTitle(workflowData.title || "Document Parsing Workflow");
        setVersion(workflowData.description || "Version 1.0");
        setIsActive(workflowData.is_active ?? true);

        const templateId = workflowData._id || workflowData.id;
        setWorkflowDbId(templateId);
        localStorage.setItem("template_id", templateId);

        const processedNodes = processImportedNodes(workflowData.nodes);
        // Inject template_id into HTTP and OneDrive nodes
        const nodesWithTemplateId = injectTemplateIdToHttpNodes(
          processedNodes,
          templateId,
        );
        setNodes(nodesWithTemplateId);

        const processedEdges = processInitialEdges(workflowData.edges);
        setEdges(
          processedEdges.map((edge) => ({
            ...edge,
            data: { ...edge.data, onEdgeDelete: onEdgesDelete },
          })),
        );

        setIsDirty(false);
        console.log("✅ Workflow loaded successfully:", workflowData);
        showToast("✅ Workflow loaded successfully!", "success");
      }
    } catch (error: any) {
      console.error("❌ Failed to load workflow:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to load workflow.";
      showToast(`Error: ${errorMessage}`, "error");
      navigate("/workflow");
    } finally {
      setIsLoadingWorkflow(false);
    }
  };

  useEffect(() => {
    if (!isDirty && !isLoadingWorkflow) setIsDirty(true);
  }, [nodes, edges, title, version, isActive]);

  const onNodesChange = useCallback(
    (changes: NodeChange<any>[]) =>
      setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );

  const onEdgesChange = useCallback(
    (changes: any[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  );

  const onEdgesDelete = useCallback((edgeId: string) => {
    setEdges((eds) => eds.filter((edge) => edge.id !== edgeId));
  }, []);

  const onConnect = useCallback(
    (connection: any) => {
      setEdges((eds) => {
        const newEdge = {
          id: `edge-${connection.source}-${connection.target}-${Date.now()}`,
          source: connection.source,
          target: connection.target,
          sourceHandle: connection.sourceHandle,
          targetHandle: connection.targetHandle,
          animated: false,
          type: "deleteButtonEdge",
          data: { onEdgeDelete: onEdgesDelete },
        };
        return [...eds, newEdge];
      });
    },
    [onEdgesDelete],
  );

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node as Node<any>);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleSaveWorkflow = useCallback(
    (skipValidation: boolean = false) => {
      if (nodes.length === 0) {
        setValidationErrors("Workflow must contain at least one node.");
        showToast("⚠️ Workflow must contain at least one node", "error");
        return;
      }

      if (!skipValidation) {
        const validation = validateWorkflow(nodes, edges);
        if (!validation.isValid) {
          const errorMessage = formatValidationErrors(validation);
          setValidationErrors(errorMessage);
          console.warn("⚠️ Validation errors:", errorMessage);
          showToast(
            "⚠️ Some nodes have validation errors. Check console for details.",
            "error",
          );
          return null;
        }
      }

      const outgoingMap: Record<string, string[]> = {};
      const incomingCount: Record<string, number> = {};
      nodes.forEach((node) => {
        outgoingMap[node.id] = [];
        incomingCount[node.id] = 0;
      });
      edges.forEach((edge) => {
        if (edge.source && edge.target) {
          outgoingMap[edge.source].push(edge.target);
          incomingCount[edge.target] = (incomingCount[edge.target] || 0) + 1;
        }
      });

      const startNode = nodes.find(
        (node) => (incomingCount[node.id] || 0) === 0,
      );
      if (!startNode) {
        console.warn("⚠️ No start node found — all nodes have incoming edges!");
        return;
      }

      const visited = new Set<string>();
      const orderedNodes: Node<any>[] = [];
      const queue: string[] = [startNode.id];
      while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (visited.has(currentId)) continue;
        visited.add(currentId);
        const node = nodes.find((n) => n.id === currentId);
        if (node) orderedNodes.push(node);
        (outgoingMap[currentId] || []).forEach((nextId) => {
          if (!visited.has(nextId)) queue.push(nextId);
        });
      }

      const workflowPayload = orderedNodes.map((node, index) => {
        const nodeDataForPayload = { ...node.data };

        // Special handling for SWITCH node transformation
        // Check for rules array specifically to identify switch logic regardless of strict type casing
        if (
          (node.type === "SWITCH" || node.type?.toUpperCase() === "SWITCH") &&
          Array.isArray(nodeDataForPayload.rules)
        ) {
          nodeDataForPayload.cases = nodeDataForPayload.rules.map(
            (rule: any, idx: number) => ({
              output_index: idx,
              conditions:
                rule.operator === "else"
                  ? []
                  : [
                      {
                        key: rule.value,
                        operator: rule.operator,
                        value: rule.compareValue,
                      },
                    ],
            }),
          );
          delete nodeDataForPayload.rules;
        }
        delete nodeDataForPayload.outputs;
        delete nodeDataForPayload.executionStatus;
        delete nodeDataForPayload.name; // Remove name from inputs as it's at top level

        // Force HTTP nodes to use "HttpNode" as name
        const nodeName =
          node.type === "HTTP" ? "HttpNode" : node.data.name || node.id;

        return {
          id: node.id,
          index: index + 1,
          name: nodeName,
          type: getNodeTypeEnum(node.type || ""),
          position: {
            x: Math.round(node.position.x),
            y: Math.round(node.position.y),
          },
          data: {
            inputs: nodeDataForPayload,
          },
        };
      });

      const nodeOrderMap = new Map(
        orderedNodes.map((node, index) => [node.id, index]),
      );
      const sortedEdges = [...edges].sort((a, b) => {
        const orderA = nodeOrderMap.get(a.source) ?? Infinity;
        const orderB = nodeOrderMap.get(b.source) ?? Infinity;
        return orderA - orderB;
      });

      const edgesPayload = [
        { source: "START", target: startNode.id },
        ...sortedEdges.map((edge) => {
          const payload: any = {
            source: edge.source,
            target: edge.target,
          };
          if (edge.sourceHandle) payload.sourceHandle = edge.sourceHandle;
          if (edge.targetHandle) payload.targetHandle = edge.targetHandle;
          return payload;
        }),
        // Connect ALL leaf nodes to END (exclude error handle edges when finding leaves)
        ...orderedNodes
          .filter(
            (node) =>
              !edges.some(
                (edge) =>
                  edge.source === node.id && edge.sourceHandle !== "error",
              ),
          )
          .map((node) => ({
            source: node.id,
            target: "END",
          })),
      ];

      const finalWorkflow = {
        title,
        description: version,
        nodes: workflowPayload,
        edges: edgesPayload,
      };

      console.log(
        "🔥 Final Workflow Payload:",
        JSON.stringify(finalWorkflow, null, 2),
      );

      return finalWorkflow;
    },
    [nodes, edges, title, version, setValidationErrors, showToast],
  );

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleExportWorkflow = useCallback(() => {
    if (nodes.length === 0) {
      showToast("⚠️ Nothing to export. Add at least one node.", "error");
      return;
    }

    try {
      const exportPayload = {
        title,
        description: version,
        nodes: nodes.map((node) => {
          const nodeData = deepClone(node.data ?? {}) as any;
          delete nodeData.outputs;
          delete nodeData.executionStatus;
          delete nodeData.onExecuteNode;

          return {
            id: node.id,
            type: node.type,
            position: node.position,
            data: nodeData,
          };
        }),
        edges: edges.map((edge) => {
          const edgeData = deepClone(edge.data ?? {}) as any;
          delete edgeData?.onEdgeDelete;

          return {
            id: edge.id,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle,
            targetHandle: edge.targetHandle,
            animated: edge.animated ?? false,
            type: edge.type || "deleteButtonEdge",
            ...(edgeData && Object.keys(edgeData).length > 0
              ? { data: edgeData }
              : {}),
          };
        }),
      };

      const safeTitle = (title || "workflow")
        .trim()
        .replace(/[<>:"/\\|?*\x00-\x1F]/g, "-")
        .replace(/\s+/g, "_");
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const fileName = `${safeTitle || "workflow"}-${timestamp}.json`;

      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showToast("✅ Workflow exported successfully!", "success");
    } catch (error) {
      console.error("❌ Failed to export workflow:", error);
      showToast("❌ Failed to export workflow.", "error");
    }
  }, [nodes, edges, title, version, showToast]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonContent = e.target?.result as string;
          const workflowData = JSON.parse(jsonContent);

          if (!workflowData.nodes || !workflowData.edges) {
            alert(
              "Invalid workflow JSON format. Must contain 'nodes' and 'edges' arrays.",
            );
            return;
          }

          const processedNodes = processImportedNodes(workflowData.nodes);
          setNodes(processedNodes);
          const processedEdges = processInitialEdges(workflowData.edges);
          setEdges(
            processedEdges.map((edge) => ({
              ...edge,
              data: { ...edge.data, onEdgeDelete: onEdgesDelete },
            })),
          );
          if (workflowData.title) setTitle(workflowData.title);
          if (workflowData.description) setVersion(workflowData.description);

          setWorkflowDbId(null);

          console.log("✅ Workflow imported successfully!", workflowData);
          showToast("✅ Workflow imported successfully!", "success");
        } catch (error) {
          console.error("❌ Failed to import workflow:", error);
          alert("Failed to parse workflow JSON. Please check the file format.");
        }
      };

      reader.readAsText(file);
      event.target.value = "";
    },
    [onEdgesDelete],
  );

  const addNode = useCallback(
    (type: string) => {
      const newNode = createNodeByType(
        type,
        {
          x: Math.random() * 400 + 300,
          y: Math.random() * 300 + 100,
        },
        nodes,
      );

      // Inject onExecuteNode callback
      newNode.data = {
        ...newNode.data,
        onExecuteNode: (id: string) => handleExecuteNodeRef.current?.(id),
      };

      setNodes((nds) => [...nds, newNode]);
      setSidebarOpen(false);
    },
    [nodes],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const type = event.dataTransfer.getData("application/reactflow");
      if (typeof type === "undefined" || !type) {
        return;
      }
      const reactFlowBounds = event.currentTarget.getBoundingClientRect();
      const position = {
        x: event.clientX - reactFlowBounds.left - 64,
        y: event.clientY - reactFlowBounds.top - 20,
      };
      const newNode = createNodeByType(type, position, nodes);
      setNodes((nds) => nds.concat(newNode));
    },
    [nodes],
  );

  const existingNodeTypes = nodes.map((node) => node.type);

  const handleBackClick = useCallback(() => {
    if (nodes.length > 0 && isDirty) {
      setShowExitConfirmModal(true);
    } else {
      navigate("/workflow");
    }
  }, [nodes.length, isDirty, navigate]);

  const handleSaveAndExit = useCallback(async () => {
    localStorage.removeItem("template_id");
    if (isSaving) return;

    setIsSaving(true);

    if (nodes.length === 0) {
      showToast("⚠️ Please add at least one node before saving.");
      setIsSaving(false);
      return;
    }

    const outgoingMap: Record<string, string[]> = {};
    const incomingCount: Record<string, number> = {};
    nodes.forEach((node) => {
      outgoingMap[node.id] = [];
      incomingCount[node.id] = 0;
    });
    edges.forEach((edge) => {
      if (edge.source && edge.target) {
        outgoingMap[edge.source].push(edge.target);
        incomingCount[edge.target] = (incomingCount[edge.target] || 0) + 1;
      }
    });

    const startNode = nodes.find((node) => (incomingCount[node.id] || 0) === 0);
    if (!startNode) {
      showToast("⚠️ Missing start node.", "error");
      setIsSaving(false);
      return;
    }

    const visited = new Set<string>();
    const orderedNodes: Node<any>[] = [];
    const queue: string[] = [startNode.id];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      const node = nodes.find((n) => n.id === currentId);
      if (node) orderedNodes.push(node);
      (outgoingMap[currentId] || []).forEach((nextId) => {
        if (!visited.has(nextId)) queue.push(nextId);
      });
    }

    const templatePayload = orderedNodes.map((node, index) => {
      const nodeDataForPayload = { ...node.data };
      delete nodeDataForPayload.outputs;
      delete nodeDataForPayload.executionStatus;
      delete nodeDataForPayload.name; // Remove name from inputs as it's at top level

      // Force HTTP nodes to use "HttpNode" as name
      const nodeName =
        node.type === "HTTP" ? "HttpNode" : node.data.name || node.id;

      return {
        id: node.id,
        index: index + 1,
        name: nodeName,
        type: getNodeTypeEnum(node.type || ""),
        position: {
          x: Math.round(node.position.x),
          y: Math.round(node.position.y),
        },
        data: {
          inputs: nodeDataForPayload,
        },
      };
    });

    const nodeOrderMap = new Map(
      orderedNodes.map((node, index) => [node.id, index]),
    );
    const sortedEdges = [...edges].sort((a, b) => {
      const orderA = nodeOrderMap.get(a.source) ?? Infinity;
      const orderB = nodeOrderMap.get(b.source) ?? Infinity;
      return orderA - orderB;
    });

    // Find terminal nodes (nodes with no outgoing edges, excluding error handle edges)
    const nodesWithOutgoing = new Set(
      sortedEdges
        .filter((edge) => edge.sourceHandle !== "error")
        .map((edge) => edge.source),
    );
    const terminalNodes = orderedNodes.filter(
      (node) => !nodesWithOutgoing.has(node.id),
    );

    // If no terminal nodes found, fall back to last ordered node
    const endNodes =
      terminalNodes.length > 0
        ? terminalNodes
        : [orderedNodes[orderedNodes.length - 1]];

    const edgesPayload = [
      { source: "START", target: startNode.id },
      ...sortedEdges.map((edge) => {
        const payload: any = {
          source: edge.source,
          target: edge.target,
        };
        if (edge.sourceHandle) payload.sourceHandle = edge.sourceHandle;
        if (edge.targetHandle) payload.targetHandle = edge.targetHandle;
        return payload;
      }),
      ...endNodes
        .filter((node) => node) // safety check
        .map((node) => ({
          source: node.id,
          target: "END",
          ...(node.type === "for-each" || node.type === "FOR-EACH"
            ? { sourceHandle: "done" }
            : {}),
        })),
    ];

    const finalTemplate = {
      title,
      description: version,
      is_active: isActive,
      nodes: templatePayload,
      edges: edgesPayload,
    };

    try {
      if (workflowDbId) {
        localStorage.setItem("template_id", workflowDbId);
        await apiClient.put(
          `${API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD}/${workflowDbId}`,
          finalTemplate,
        );
      } else {
        await apiClient.post(
          API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD,
          finalTemplate,
        );
      }

      setShowExitConfirmModal(false);
      navigate("/workflow");
    } catch (error: any) {
      console.error("❌ Failed to save workflow:", error);
      showToast(
        `Error: ${error.response?.data?.message || "Failed to save"}`,
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    nodes,
    edges,
    title,
    version,
    isActive,
    isSaving,
    workflowDbId,
    navigate,
  ]);

  const handleDiscardAndExit = useCallback(() => {
    setShowExitConfirmModal(false);
    navigate("/workflow");
  }, [navigate]);

  // Track which nodes have been updated with outputs
  const [updatedNodeIds, setUpdatedNodeIds] = useState<Set<string>>(new Set());

  // Poll execution status by ID
  const pollExecutionStatus = useCallback(
    async (workflowId: string) => {
      try {
        const response = await apiClient.get(
          API_ENDPOINTS.WORKFLOW.EXECUTION_BY_TEMPLATE_ID(workflowId),
        );

        if (response.data.success && response.data.data) {
          const executionData = response.data.data;
          const overallStatus = executionData.overall_proccessing_status;

          console.log("📊 Execution status:", overallStatus, executionData);

          // Update nodes with outputs from execution
          if (executionData.nodes && Array.isArray(executionData.nodes)) {
            setNodes((prevNodes) =>
              prevNodes.map((node) => {
                const executedNode = executionData.nodes.find(
                  (n: any) => n.id === node.id,
                );

                if (executedNode && executedNode.data) {
                  // Find node status from node_status array
                  // Match by name, id, or data.name (backend may use different naming)
                  const nodeName = executedNode.name || "";
                  const nodeDataName = node.data?.name || "";
                  const nodeStatus = executionData.node_status?.find(
                    (ns: any) =>
                      ns.node_name === nodeName ||
                      ns.node_name === executedNode.id ||
                      ns.node_name === nodeDataName ||
                      nodeName.startsWith(ns.node_name) ||
                      ns.node_name.startsWith(nodeName),
                  );

                  const status = nodeStatus?.status || "PENDING";
                  let executionStatus = null;

                  if (status === "DONE") {
                    executionStatus = "success";

                    // Check if this node just completed (wasn't updated before)
                    if (
                      !updatedNodeIds.has(node.id) &&
                      executedNode.data.outputs
                    ) {
                      console.log(
                        `✅ Node ${node.id} completed! Outputs:`,
                        executedNode.data.outputs,
                      );
                      setUpdatedNodeIds((prev) => new Set([...prev, node.id]));
                    }
                  } else if (status === "FAILED" || status === "ERROR") {
                    executionStatus = "error";
                  } else if (status === "NOT_STARTED") {
                    executionStatus = null;
                  } else {
                    // RUNNING, PENDING, PROCESSING, IN_PROGRESS, etc.
                    executionStatus = "running";
                  }

                  return {
                    ...node,
                    data: {
                      ...node.data,
                      ...executedNode.data.inputs,
                      outputs: executedNode.data.outputs || node.data.outputs,
                      executionStatus,
                      name: node.type === "HTTP" ? "HttpNode" : node.data.name,
                    },
                  };
                }

                return node;
              }),
            );
          }

          // Check if execution is complete
          if (overallStatus === "DONE" || overallStatus == "WARNING") {
            showToast("✅ Workflow executed successfully!", "success");
            setIsRunning(false);
            setPollingAttempts(0);
            setUpdatedNodeIds(new Set()); // Reset for next execution

            // Clear execution status after 3 seconds
            setTimeout(() => {
              setNodes((prevNodes) =>
                prevNodes.map((n) => ({
                  ...n,
                  data: {
                    ...n.data,
                    executionStatus: null,
                  },
                })),
              );
            }, 3000);

            return true; // Stop polling
          } else if (overallStatus === "FAILED" || overallStatus === "ERROR") {
            showToast("❌ Workflow execution failed!", "error");
            setIsRunning(false);
            setPollingAttempts(0);
            setUpdatedNodeIds(new Set()); // Reset
            return true; // Stop polling
          }

          return false; // Continue polling
        }

        return false;
      } catch (error: any) {
        console.error("❌ Failed to poll execution status:", error);
        return false;
      }
    },
    [setNodes, showToast, updatedNodeIds],
  );

  // Start polling execution status
  useEffect(() => {
    if (!executionId || !isRunning) return;

    const pollInterval = setInterval(async () => {
      if (pollingAttempts >= MAX_POLLING_ATTEMPTS) {
        console.warn("⚠️ Max polling attempts reached");
        showToast("⚠️ Execution is taking longer than expected", "error");
        setIsRunning(false);
        setPollingAttempts(0);
        clearInterval(pollInterval);
        return;
      }

      const shouldStop = await pollExecutionStatus(workflowDbId || "");
      if (shouldStop) {
        clearInterval(pollInterval);
      } else {
        setPollingAttempts((prev) => prev + 1);
      }
    }, POLLING_INTERVAL);

    return () => clearInterval(pollInterval);
  }, [executionId, isRunning, pollingAttempts, pollExecutionStatus, showToast]);

  // Auto-save when nodes or edges change (debounced)
  useEffect(() => {
    // Only auto-save if template already exists
    if (!workflowDbId || isSaving || isRunning) return;

    // Mark as having unsaved changes
    setHasUnsavedChanges(true);

    // Clear existing timeout
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }

    // Set new timeout for auto-save (2 seconds after last change)
    const timeout = setTimeout(async () => {
      if (nodes.length > 0) {
        console.log("🔄 Auto-saving template...");
        const payload = handleSaveWorkflow(false);
        if (payload) {
          try {
            localStorage.setItem("template_id", workflowDbId);
            await apiClient.put(
              `${API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD}/${workflowDbId}`,
              payload,
            );
            setHasUnsavedChanges(false);
            console.log("✅ Template auto-saved");
          } catch (error) {
            console.error("❌ Auto-save failed:", error);
          }
        }
      }
    }, 2000);

    setAutoSaveTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [nodes, edges]); // Trigger on nodes or edges change

  // Real API Execution - Replace Mock
  const handleRunWorkflow = useCallback(async () => {
    // Auto-save if there are unsaved changes
    if (!workflowDbId) {
      showToast("⚠️ Please save the template first before running", "error");
      return;
    }

    // If there are unsaved changes, save first
    if (hasUnsavedChanges) {
      console.log("🔄 Saving changes before execution...");
      const payload = handleSaveWorkflow(false);
      if (payload) {
        try {
          localStorage.setItem("template_id", workflowDbId);
          await apiClient.put(
            `${API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD}/${workflowDbId}`,
            payload,
          );
          setHasUnsavedChanges(false);
          console.log("✅ Changes saved");
        } catch (error) {
          console.error("❌ Save failed:", error);
          showToast("❌ Failed to save changes before execution");
          return;
        }
      }
    }

    setIsRunning(true);
    setExecutionResults([]);
    setPollingAttempts(0);
    setUpdatedNodeIds(new Set()); // Reset tracking for new execution

    setNodes((prevNodes) =>
      prevNodes.map((n) => ({
        ...n,
        data: {
          ...n.data,
          executionStatus: "running",
        },
      })),
    );

    try {
      console.log("🚀 Starting workflow execution...");

      // Prepare execution payload - must include full node data with inputs
      const outgoingMap: Record<string, string[]> = {};
      const incomingCount: Record<string, number> = {};
      nodes.forEach((node) => {
        outgoingMap[node.id] = [];
        incomingCount[node.id] = 0;
      });
      edges.forEach((edge) => {
        if (edge.source && edge.target) {
          outgoingMap[edge.source].push(edge.target);
          incomingCount[edge.target] = (incomingCount[edge.target] || 0) + 1;
        }
      });

      const startNode = nodes.find(
        (node) => (incomingCount[node.id] || 0) === 0,
      );
      if (!startNode) {
        showToast("⚠️ No start node found", "error");
        setIsRunning(false);
        return;
      }

      const visited = new Set<string>();
      const orderedNodes: Node<any>[] = [];
      const queue: string[] = [startNode.id];
      while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (visited.has(currentId)) continue;
        visited.add(currentId);
        const node = nodes.find((n) => n.id === currentId);
        if (node) orderedNodes.push(node);
        (outgoingMap[currentId] || []).forEach((nextId) => {
          if (!visited.has(nextId)) queue.push(nextId);
        });
      }

      const executionPayload = orderedNodes.map((node, index) => {
        const nodeDataForPayload = { ...node.data };
        delete nodeDataForPayload.outputs;
        delete nodeDataForPayload.executionStatus;
        delete nodeDataForPayload.name; // Remove name from inputs as it's at top level

        // Force HTTP nodes to use "HttpNode" as name
        const nodeName =
          node.type === "HTTP" ? "HttpNode" : node.data.name || node.id;

        return {
          id: node.id,
          index: index + 1,
          name: nodeName,
          type: getNodeTypeEnum(node.type || ""),
          position: {
            x: Math.round(node.position.x),
            y: Math.round(node.position.y),
          },
          data: {
            inputs: nodeDataForPayload,
          },
        };
      });

      const nodeOrderMap = new Map(
        orderedNodes.map((node, index) => [node.id, index]),
      );
      const sortedEdges = [...edges].sort((a, b) => {
        const orderA = nodeOrderMap.get(a.source) ?? Infinity;
        const orderB = nodeOrderMap.get(b.source) ?? Infinity;
        return orderA - orderB;
      });

      // Find terminal nodes (nodes with no outgoing edges, excluding error handle edges)
      const execNodesWithOutgoing = new Set(
        sortedEdges
          .filter((edge) => edge.sourceHandle !== "error")
          .map((edge) => edge.source),
      );
      const execTerminalNodes = orderedNodes.filter(
        (node) => !execNodesWithOutgoing.has(node.id),
      );
      const execEndNodes =
        execTerminalNodes.length > 0
          ? execTerminalNodes
          : [orderedNodes[orderedNodes.length - 1]];

      const edgesPayload = [
        { source: "START", target: startNode.id },
        ...sortedEdges.map((edge) => {
          const payload: any = {
            source: edge.source,
            target: edge.target,
          };
          if (edge.sourceHandle) payload.sourceHandle = edge.sourceHandle;
          if (edge.targetHandle) payload.targetHandle = edge.targetHandle;
          return payload;
        }),
        ...execEndNodes
          .filter((node) => node)
          .map((node) => ({
            source: node.id,
            target: "END",
          })),
      ];

      const finalExecutionPayload = {
        template_id: workflowDbId,
        nodes: executionPayload,
        edges: edgesPayload,
      };

      console.log(
        "📤 Execution payload:",
        JSON.stringify(finalExecutionPayload, null, 2),
      );

      // Call execution API
      const executionResponse = await apiClient.post(
        API_ENDPOINTS.WORKFLOW.EXECUTION,
        finalExecutionPayload,
      );

      if (executionResponse.data.success && executionResponse.data.data) {
        const execId =
          executionResponse.data.data._id || executionResponse.data.data.id;
        console.log("✅ Execution started with ID:", execId);
        setExecutionId(execId);

        // Start polling immediately
        const shouldStop = await pollExecutionStatus(workflowDbId);
        if (!shouldStop) {
          // Polling will continue via useEffect
          console.log("🔄 Polling started...");
        }
      } else {
        throw new Error("Failed to start execution");
      }
    } catch (error: any) {
      console.error("❌ Failed to execute workflow:", error);

      // Mark all nodes as error
      setNodes((prevNodes) =>
        prevNodes.map((n) => ({
          ...n,
          data: {
            ...n.data,
            executionStatus: "error",
          },
        })),
      );

      // Extract error message from different possible structures
      let errorMessage = "Failed to execute workflow. Please try again.";

      if (error.response?.data?.error?.error_description) {
        // Handle structure: { success: false, error: { error_code, error_description } }
        const errorData = error.response.data.error;
        errorMessage = `${errorData.error_code || "Error"}: ${
          errorData.error_description
        }`;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        // If error is a string
        errorMessage =
          typeof error.response.data.error === "string"
            ? error.response.data.error
            : JSON.stringify(error.response.data.error);
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(`❌ ${errorMessage}`, "error");
      setIsRunning(false);
    }
  }, [
    workflowDbId,
    nodes,
    edges,
    setNodes,
    showToast,
    pollExecutionStatus,
    hasUnsavedChanges,
    handleSaveWorkflow,
  ]);

  // Execute workflow up to a specific node
  const handleExecuteNode = useCallback(
    async (targetNodeId: string) => {
      // Auto-save if there are unsaved changes
      if (!workflowDbId) {
        showToast("⚠️ Please save the template first before running", "error");
        return;
      }

      if (hasUnsavedChanges) {
        console.log("🔄 Saving changes before partial execution...");
        const payload = handleSaveWorkflow(false);
        if (payload) {
          try {
            localStorage.setItem("template_id", workflowDbId);
            await apiClient.put(
              `${API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD}/${workflowDbId}`,
              payload,
            );
            setHasUnsavedChanges(false);
            console.log("✅ Changes saved");
          } catch (error) {
            console.error("❌ Save failed:", error);
            showToast("❌ Failed to save changes before execution", "error");
            return;
          }
        }
      }

      setIsRunning(true);
      setExecutionResults([]);
      setPollingAttempts(0);
      // Don't reset updatedNodeIds completely, maybe just for the path?
      // For simplicity, let's reset to ensure fresh status for this run
      setUpdatedNodeIds(new Set());

      // Mark all nodes as running so long-running nodes (e.g. WebScraper) show loading immediately
      setNodes((prevNodes) =>
        prevNodes.map((n) => ({
          ...n,
          data: {
            ...n.data,
            executionStatus: "running",
          },
        })),
      );

      try {
        console.log(
          `🚀 Starting partial execution up to node ${targetNodeId}...`,
        );

        // 1. Identify ancestors
        const ancestors = new Set<string>();
        const queue = [targetNodeId];
        ancestors.add(targetNodeId);

        // Build reverse adjacency list for easier traversal up
        const incomingMap: Record<string, string[]> = {};
        edges.forEach((edge) => {
          if (!incomingMap[edge.target]) incomingMap[edge.target] = [];
          incomingMap[edge.target].push(edge.source);
        });

        while (queue.length > 0) {
          const current = queue.shift()!;
          const parents = incomingMap[current] || [];
          parents.forEach((parent) => {
            if (!ancestors.has(parent)) {
              ancestors.add(parent);
              queue.push(parent);
            }
          });
        }

        // 2. Filter nodes and edges
        const partialNodes = nodes.filter((n) => ancestors.has(n.id));
        const partialEdges = edges.filter(
          (e) => ancestors.has(e.source) && ancestors.has(e.target),
        );

        // 3. Topological sort for the partial graph
        const outgoingMap: Record<string, string[]> = {};
        const incomingCount: Record<string, number> = {};
        partialNodes.forEach((node) => {
          outgoingMap[node.id] = [];
          incomingCount[node.id] = 0;
        });
        partialEdges.forEach((edge) => {
          outgoingMap[edge.source].push(edge.target);
          incomingCount[edge.target] = (incomingCount[edge.target] || 0) + 1;
        });

        const startNode = partialNodes.find(
          (node) => (incomingCount[node.id] || 0) === 0,
        );

        if (!startNode) {
          showToast("⚠️ Could not find a start node for this path", "error");
          setIsRunning(false);
          return;
        }

        const visited = new Set<string>();
        const orderedNodes: Node<any>[] = [];
        const sortQueue: string[] = [startNode.id];

        while (sortQueue.length > 0) {
          const currentId = sortQueue.shift()!;
          if (visited.has(currentId)) continue;
          visited.add(currentId);
          const node = partialNodes.find((n) => n.id === currentId);
          if (node) orderedNodes.push(node);
          (outgoingMap[currentId] || []).forEach((nextId) => {
            // Only traverse if nextId is part of our partial graph
            if (ancestors.has(nextId) && !visited.has(nextId)) {
              sortQueue.push(nextId);
            }
          });
        }

        // 4. Construct Payload
        const executionPayload = orderedNodes.map((node, index) => {
          const nodeDataForPayload = { ...node.data };
          delete nodeDataForPayload.outputs;
          delete nodeDataForPayload.executionStatus;
          delete nodeDataForPayload.name;

          const nodeName =
            node.type === "HTTP" ? "HttpNode" : node.data.name || node.id;

          return {
            id: node.id,
            index: index + 1,
            name: nodeName,
            type: getNodeTypeEnum(node.type || ""),
            position: {
              x: Math.round(node.position.x),
              y: Math.round(node.position.y),
            },
            data: {
              inputs: nodeDataForPayload,
            },
          };
        });

        const nodeOrderMap = new Map(
          orderedNodes.map((node, index) => [node.id, index]),
        );
        const sortedEdges = [...partialEdges].sort((a, b) => {
          const orderA = nodeOrderMap.get(a.source) ?? Infinity;
          const orderB = nodeOrderMap.get(b.source) ?? Infinity;
          return orderA - orderB;
        });

        const edgesPayload = [
          { source: "START", target: startNode.id },
          ...sortedEdges.map((edge) => ({
            source: edge.source,
            target: edge.target,
            ...(edge.sourceHandle ? { sourceHandle: edge.sourceHandle } : {}),
            ...(edge.targetHandle ? { targetHandle: edge.targetHandle } : {}),
          })),
          {
            source: targetNodeId, // End at the target node
            target: "END",
          },
        ];

        const finalExecutionPayload = {
          template_id: workflowDbId,
          nodes: executionPayload,
          edges: edgesPayload,
        };

        console.log(
          "📤 Partial Execution payload:",
          JSON.stringify(finalExecutionPayload, null, 2),
        );

        // 5. Call API
        const executionResponse = await apiClient.post(
          API_ENDPOINTS.WORKFLOW.EXECUTION,
          finalExecutionPayload,
        );

        if (executionResponse.data.success && executionResponse.data.data) {
          const execId =
            executionResponse.data.data._id || executionResponse.data.data.id;
          console.log("✅ Partial Execution started with ID:", execId);
          setExecutionId(execId);

          const shouldStop = await pollExecutionStatus(workflowDbId);
          if (!shouldStop) {
            console.log("🔄 Polling started...");
          }
        } else {
          throw new Error("Failed to start partial execution");
        }
      } catch (error: any) {
        console.error("❌ Failed to execute partial workflow:", error);
        showToast("❌ Failed to execute partial workflow", "error");
        setIsRunning(false);
      }
    },
    [
      workflowDbId,
      nodes,
      edges,
      setNodes,
      showToast,
      pollExecutionStatus,
      hasUnsavedChanges,
      handleSaveWorkflow,
    ],
  );

  // Keep refs synced with the latest functions
  useEffect(() => {
    handleExecuteNodeRef.current = handleExecuteNode;
  }, [handleExecuteNode]);

  const handleSaveTemplate = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);

    if (nodes.length === 0) {
      showToast("⚠️ Please add at least one node before saving.");
      setIsSaving(false);
      return;
    }

    const outgoingMap: Record<string, string[]> = {};
    const incomingCount: Record<string, number> = {};
    nodes.forEach((node) => {
      outgoingMap[node.id] = [];
      incomingCount[node.id] = 0;
    });
    edges.forEach((edge) => {
      if (edge.source && edge.target) {
        outgoingMap[edge.source].push(edge.target);
        incomingCount[edge.target] = (incomingCount[edge.target] || 0) + 1;
      }
    });

    const startNode = nodes.find((node) => (incomingCount[node.id] || 0) === 0);
    if (!startNode) {
      showToast(
        "⚠️ Missing start node. Make sure at least one node has no incoming edge.",
        "error",
      );
      setIsSaving(false);
      return;
    }

    const visited = new Set<string>();
    const orderedNodes: Node<any>[] = [];
    const queue: string[] = [startNode.id];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      const node = nodes.find((n) => n.id === currentId);
      if (node) orderedNodes.push(node);
      (outgoingMap[currentId] || []).forEach((nextId) => {
        if (!visited.has(nextId)) queue.push(nextId);
      });
    }

    const templatePayload = orderedNodes.map((node, index) => {
      const nodeDataForPayload = { ...node.data };
      delete nodeDataForPayload.outputs;
      delete nodeDataForPayload.executionStatus;
      delete nodeDataForPayload.name; // Remove name from inputs as it's at top level

      // Force HTTP nodes to use "HttpNode" as name
      const nodeName =
        node.type === "HTTP" ? "HttpNode" : node.data.name || node.id;

      return {
        id: node.id,
        index: index + 1,
        name: nodeName,
        type: getNodeTypeEnum(node.type || ""),
        position: {
          x: Math.round(node.position.x),
          y: Math.round(node.position.y),
        },
        data: {
          inputs: nodeDataForPayload,
        },
      };
    });

    const nodeOrderMap = new Map(
      orderedNodes.map((node, index) => [node.id, index]),
    );
    const sortedEdges = [...edges].sort((a, b) => {
      const orderA = nodeOrderMap.get(a.source) ?? Infinity;
      const orderB = nodeOrderMap.get(b.source) ?? Infinity;
      return orderA - orderB;
    });

    // Find terminal nodes (exclude error handle edges)
    const saveNodesWithOutgoing = new Set(
      sortedEdges
        .filter((edge) => edge.sourceHandle !== "error")
        .map((edge) => edge.source),
    );
    const saveTerminalNodes = orderedNodes.filter(
      (node) => !saveNodesWithOutgoing.has(node.id),
    );
    const saveEndNodes =
      saveTerminalNodes.length > 0
        ? saveTerminalNodes
        : [orderedNodes[orderedNodes.length - 1]];

    const edgesPayload = [
      { source: "START", target: startNode.id },
      ...sortedEdges.map((edge) => ({
        source: edge.source,
        target: edge.target,
        ...(edge.sourceHandle ? { sourceHandle: edge.sourceHandle } : {}),
        ...(edge.targetHandle ? { targetHandle: edge.targetHandle } : {}),
      })),
      ...saveEndNodes
        .filter((node) => node)
        .map((node) => ({
          source: node.id,
          target: "END",
        })),
    ];

    const finalTemplate = {
      title,
      description: version,
      is_active: isActive,
      nodes: templatePayload,
      edges: edgesPayload,
    };

    try {
      console.log("🚀 Saving workflow with payload:", finalTemplate);

      let response;
      if (workflowDbId) {
        localStorage.setItem("template_id", workflowDbId);
        response = await apiClient.put(
          `${API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD}/${workflowDbId}`,
          finalTemplate,
        );
        showToast("🎉 Template updated successfully!", "success");
      } else {
        response = await apiClient.post(
          API_ENDPOINTS.WORKFLOW.WORKFLOW_CRUD,
          finalTemplate,
        );
        showToast("🎉 Template created successfully!", "success");

        if (response.data.success && response.data.data) {
          const savedId = response.data.data._id || response.data.data.id;
          setWorkflowDbId(savedId);
          console.log("💾 Workflow ID stored:", savedId);
          localStorage.setItem("template_id", savedId);
          setNodes((prev) =>
            prev.map((node) => {
              if (node.type === "HTTP") {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    name: "HttpNode", // Always set name to HttpNode
                    request_id: savedId, // ⭐ inject backend _id
                  },
                };
              }
              if (
                node.type === "ONEDRIVE-CONNECTOR" ||
                node.type === "ONEDRIVE-UPLOAD"
              ) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    template_id: savedId, // ⭐ inject template_id for OneDrive auth
                  },
                };
              }
              return node;
            }),
          );
        }
      }

      setIsDirty(false);
    } catch (error: any) {
      console.error("❌ Failed to save workflow:", error);

      const apiError = error.response?.data?.error;
      const statusCode = error.response?.status;

      if (statusCode === 409 && apiError?.error_code === "duplicate_title") {
        showToast(
          `${
            apiError.error_description ||
            "Failed to save workflow. Please try again."
          } `,
        );
      } else {
        const errorMessage =
          apiError?.error_description ||
          error.response?.data?.message ||
          error.message ||
          "Failed to save workflow. Please try again.";
        showToast(`Error: ${errorMessage}`, "error");
      }
    } finally {
      setIsSaving(false);
    }
  }, [nodes, edges, title, version, isActive, isSaving, workflowDbId]);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <ToastNotification
        message={toastMessage}
        onClose={() => setToastMessage(null)}
        type={toastType}
      />

      <nav className="fixed top-0 left-0 right-0 z-40 h-[66px] bg-[#fafafa] border-b border-gray-200 flex items-center justify-between pe-3 ps-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={handleBackClick}
            title="Go Back"
            className="w-9 h-9"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </Button>
          <div className="flex items-center justify-center w-9 h-9 bg-blue-100 border border-gray-200 rounded-md">
            <Workflow className="w-5 h-5 text-gray-800" />
          </div>
          <div className="flex flex-col leading-tight">
            <WorkflowTitle
              title={title}
              onTitleChange={setTitle}
              description={version}
              onDescriptionChange={setVersion}
            />
          </div>
        </div>

        {/* Center Tab Switcher */}
        <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <Button
            onClick={() => setActiveTab("nodes")}
            variant={activeTab === "nodes" ? "primary" : "ghost"}
            size="sm"
            className="px-6 rounded-none first:rounded-l-lg last:rounded-r-lg"
          >
            <div className="flex items-center gap-2">
              <Workflow className="w-4 h-4" />
              Nodes
            </div>
          </Button>
          <Button
            onClick={() => setActiveTab("executions")}
            variant={activeTab === "executions" ? "primary" : "ghost"}
            size="sm"
            className="px-6 rounded-none first:rounded-l-lg last:rounded-r-lg relative"
          >
            <div className="flex items-center gap-2">
              <PlayCircle className="w-4 h-4" />
              Executions
              {executionResults.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 text-white text-xs rounded-full flex items-center justify-center">
                  {executionResults.length}
                </span>
              )}
            </div>
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileChange}
            className="hidden"
          />
          <div className="flex items-center gap-2">
            <label className="flex items-center cursor-pointer">
              <span className="me-2 text-sm text-gray-700">
                {isActive ? "Active" : "Inactive"}
              </span>
              <div className="relative me-3">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`block w-10 h-5 rounded-full transition-colors duration-300 ease-in-out border ${
                    isActive
                      ? "bg-blue-600 border-blue-600 shadow-inner"
                      : "bg-slate-200 border-slate-300"
                  }`}
                ></div>
                <div
                  className={`dot absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform duration-300 ease-in-out shadow-sm ${
                    isActive ? "translate-x-5" : ""
                  }`}
                ></div>
              </div>
            </label>
          </div>

          {workflowDbId && activeTab !== "executions" && (
            <Button
              onClick={() => setShowTokenAnalytics(true)}
              variant="secondary"
              size="sm"
              title="Token Usage Analytics"
              leftIcon={<Cpu className="w-4 h-4" />}
            >
              Token Analytics
            </Button>
          )}
          <Button
            onClick={handleImportClick}
            variant="secondary"
            size="sm"
            title="Import Workflow JSON"
            leftIcon={<Upload className="w-4 h-4" />}
          >
            Import
          </Button>
          <Button
            onClick={handleExportWorkflow}
            variant="secondary"
            size="sm"
            title="Export Workflow JSON"
            leftIcon={<Download className="w-4 h-4" />}
          >
            Export
          </Button>
          <Can anyOf={["workflows:create", "workflows:edit"]}>
            <Button
              onClick={handleSaveTemplate}
              disabled={!isDirty || isSaving}
              isLoading={isSaving}
              variant="primary"
              size="sm"
              leftIcon={!isSaving ? <Save className="w-4 h-4" /> : undefined}
              className={
                !isDirty || isSaving
                  ? "bg-gray-200 text-gray-400 hover:bg-gray-200"
                  : ""
              }
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </Can>
        </div>
      </nav>

      <div className="flex-1 pt-16 flex">
        {/* Nodes View */}
        {activeTab === "nodes" && (
          <div
            className="flex-1 relative"
            onDragOver={onDragOver}
            onDrop={onDrop}
          >
            {sidebarOpen && (
              <div
                className="fixed inset-0 z-40"
                onClick={() => setSidebarOpen(false)}
              />
            )}
            {sidebarOpen && (
              <div className="fixed left-0 top-0 h-full z-50 transform transition-transform duration-300 ease-out">
                <LeftSidebar
                  onAddNode={addNode}
                  onClose={() => setSidebarOpen(false)}
                  existingNodes={existingNodeTypes as any}
                />
              </div>
            )}
            <Button
              onClick={() => setSidebarOpen(true)}
              variant="secondary"
              className="fixed top-20 left-4 z-30 w-14 h-14 p-0"
              aria-label="Add Node"
            >
              <Plus className="w-6 h-6 text-gray-600" />
            </Button>
            <ReactFlowProvider>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={onNodeClick}
                onPaneClick={onPaneClick}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                fitView
                fitViewOptions={{ padding: 0.2, maxZoom: 0.7 }}
                className="bg-gray-50"
                panOnScroll={true}
                selectionOnDrag={true}
                nodesDraggable
                nodesConnectable
                elementsSelectable
                selectNodesOnDrag={false}
                panOnDrag={[1, 2]}
                zoomOnDoubleClick={false}
                proOptions={proOptions}
              >
                <Controls
                  orientation="horizontal"
                  className="bg-white shadow-lg rounded-lg border border-gray-200"
                />
                <MiniMap
                  className="bg-white shadow-lg rounded-lg border border-gray-200"
                  nodeColor="#e5e7eb"
                  maskColor="rgba(0,0,0,0.1)"
                />
                <Background
                  id="1"
                  gap={10}
                  color="#969595ff"
                  variant={BackgroundVariant.Dots}
                />
              </ReactFlow>
            </ReactFlowProvider>
            <Can permission="workflows:execute">
              <Button
                onClick={handleRunWorkflow}
                disabled={isRunning}
                isLoading={isRunning}
                variant="gradient"
                size="md"
                className="fixed bottom-8 left-1/2 -translate-x-1/2 z-30"
                leftIcon={
                  !isRunning ? <RocketIcon className="w-4 h-4" /> : undefined
                }
              >
                {isRunning ? "Executing Workflow..." : "Run Workflow"}
              </Button>
            </Can>
          </div>
        )}

        {/* Executions View */}
        {activeTab === "executions" && (
          <div className="flex-1 bg-white overflow-hidden">
            <ExecutionsPanel
              nodes={nodes}
              isRunning={isRunning}
              executionId={executionId}
              templateId={workflowDbId || undefined}
              workflowName={title}
            />
          </div>
        )}
      </div>

      <ExitConfirmationModal
        isOpen={showExitConfirmModal}
        onClose={() => setShowExitConfirmModal(false)}
        onSaveAndExit={handleSaveAndExit}
        onDiscardAndExit={handleDiscardAndExit}
      />

      {showTokenAnalytics && workflowDbId && (
        <WorkflowTokenAnalytics
          templateId={workflowDbId}
          workflowName={title}
          onClose={() => setShowTokenAnalytics(false)}
        />
      )}
    </div>
  );
}

export default DocumentParsingWorkflow;
